import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DashboardService } from '../dashboard.service';
import { TranslateService } from '../../../services/translate.service';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { Subscription, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressBarModule } from 'primeng/progressbar';
import { ButtonModule } from 'primeng/button';

@Component({
  standalone: true,
  selector: 'app-uploaded-files-dialog',
  imports: [CommonModule, FormsModule, TooltipModule, ProgressBarModule, ButtonModule],
  templateUrl: './uploaded-files-dialog.component.html',
  styleUrls: ['./uploaded-files-dialog.component.scss'],
  providers: [DatePipe]
})
export class UploadedFilesDialogComponent implements OnInit, OnDestroy {
  allApiData: any = {};
  totalFiles: number = 0;
  newFiles: number = 0;

  uploadsMap: Record<string, number> = {};
  weeks: Array<Array<{ date: string; count: number }>> = [];
  loading = false;
  error: string | null = null;

  // Summary data for Tables 2-6
  uploadedThisYear: number = 0;
  uploadedThisMonth: number = 0;
  uploadedThisWeek: number = 0;
  uploadedThisDay: number = 0;
  uploadedThisHour: number = 0;

  // Dynamic colors
  userPrimaryColor: string = '#007ad9';
  defaultColor :string = '#007ad9';
  lowColor: string = '';
  mediumColor: string = '';
  highColor: string = '';

  private sub: Subscription | null = null;
  private caller: string;

  constructor(
    private dashboardService: DashboardService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private datePipe: DatePipe,
    public translateServ : TranslateService
  ) {
    this.caller = (config?.data?.caller as string) ?? (this.constructor.name.substring(1));
  }

  ngOnInit(): void {
    // Read primary color from CSS variable
    const rootStyles = getComputedStyle(document.documentElement); 
    let primary = rootStyles.getPropertyValue('--primary-color') || this.defaultColor;
    if(primary === '#fafafa')
    {
        primary = this.defaultColor;
    }
    this.userPrimaryColor = primary.trim();

    this.lowColor = this.lightenColor(this.userPrimaryColor, 50);
    this.mediumColor = this.userPrimaryColor;
    this.highColor = this.lightenColor(this.userPrimaryColor, -25);

    this.loadData();
  }

  private lightenColor(color: string, percent: number): string {
    const num = parseInt(color.replace('#',''),16);
    const r = Math.min(255, ((num >> 16) & 0xFF) + (255 - ((num >> 16) & 0xFF)) * percent / 100);
    const g = Math.min(255, ((num >> 8) & 0xFF) + (255 - ((num >> 8) & 0xFF)) * percent / 100);
    const b = Math.min(255, (num & 0xFF) + (255 - (num & 0xFF)) * percent / 100);
    return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
  }

  loadData(): void {
    this.loading = true;
    this.error = null;

    this.sub = this.dashboardService.getExtendedData(this.caller)
      .pipe(
        catchError(err => {
          console.error('UploadedFilesDialog - API error', err);
          this.error = 'Failed to load data';
          this.loading = false;
          return of(null);
        })
      )
      .subscribe((data: any) => {
        this.loading = false;
        if (!data) return;

        this.allApiData = data;
        this.totalFiles = data?.Table?.[0]?.TotalFiles ?? 0;
        this.newFiles = (data?.Table1 ?? []).reduce(
          (sum: number, rec: any) => sum + (rec?.Uploaded_Files ?? 0),
          0
        );

        // Summary tables 2-6
        this.uploadedThisYear = data?.Table2?.[0]?.Total_in_Current_Year ?? 0;
        this.uploadedThisMonth = data?.Table3?.[0]?.Total_in_Current_Month ?? 0;
        this.uploadedThisWeek = data?.Table4?.[0]?.Total_in_Current_Week ?? 0;
        this.uploadedThisDay = data?.Table5?.[0]?.Total_in_Current_Day ?? 0;
        this.uploadedThisHour = data?.Table6?.[0]?.Total_in_Current_Hour ?? 0;

        this.buildUploadsMap();
        this.buildActivityGrid();
      });
  }

  private buildUploadsMap() {
    this.uploadsMap = {};
    const table1 = this.allApiData?.Table1 ?? [];
    for (const item of table1 as Array<{ Date: string; Uploaded_Files?: number }>) {
      const key = item.Date; // expected format 'YYYY-MM-DD'
      if (!this.uploadsMap[key]) this.uploadsMap[key] = 0;
      this.uploadsMap[key] += item.Uploaded_Files ?? 0;
    }
  }

  private buildActivityGrid() {
    const today = new Date();
    const oneYearAgo = new Date(today);
    oneYearAgo.setFullYear(today.getFullYear() - 1);

    const startDate = new Date(oneYearAgo);
    startDate.setDate(oneYearAgo.getDate() - oneYearAgo.getDay());

    this.weeks = [];
    let week: Array<{ date: string; count: number }> = [];
    const current = new Date(startDate);

    while (current <= today) {
      const dateStr = this.datePipe.transform(current, 'yyyy-MM-dd')!;
      const count = this.uploadsMap[dateStr] || 0;

      week.push({ date: dateStr, count });

      if (week.length === 7) {
        this.weeks.push(week);
        week = [];
      }

      current.setDate(current.getDate() + 1);
    }

    if (week.length > 0) this.weeks.push(week);
  }

  getHeatColor(count: number): string {
    if (!count) return '#eee';
    if (count < 5) return this.lowColor;
    if (count < 15) return this.mediumColor;
    return this.highColor;
  }
  
  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}